import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, photoFileUrl, committeePhotoUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import {
  LogOut, Clock, Megaphone, Image as ImageIcon, Calendar, BookOpen, Settings, Plus, Trash2, Save, Upload, Pin, Users, HandHeart, CreditCard, Receipt, ShieldCheck
} from "lucide-react";
import { CausesManager, PaymentSettingsManager, DonationsManager } from "@/pages/admin/FundraisingManagers";
import { SecuritySection } from "@/pages/admin/SecurityManagers";

const TABS = [
  { id: "prayer", label: "Prayer Times", icon: Clock },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "photos", label: "Photo Gallery", icon: ImageIcon },
  { id: "events", label: "Events", icon: Calendar },
  { id: "committee", label: "Committee", icon: Users },
  { id: "causes", label: "Causes", icon: HandHeart },
  { id: "payments", label: "Payment Settings", icon: CreditCard },
  { id: "donations", label: "Donations", icon: Receipt },
  { id: "content", label: "Site Content", icon: Settings },
  { id: "security", label: "Security", icon: ShieldCheck },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("prayer");

  const handleLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-emerald-950 text-stone-200 hidden md:flex md:flex-col">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500 grid place-items-center text-emerald-950 font-serif-en text-xl">ن</div>
            <div>
              <div className="font-serif-en text-lg">Al Noor</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300">CMS</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all ${
                tab === id ? "bg-amber-500 text-emerald-950 font-medium" : "hover:bg-white/5"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-4 py-2 text-xs text-stone-400">{user?.email}</div>
          <button data-testid="logout-btn" onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2.5 rounded-md text-sm hover:bg-white/5">
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 max-w-7xl">
        <div className="md:hidden flex items-center justify-between mb-6">
          <h1 className="font-serif-en text-2xl text-emerald-950">{t("admin_dashboard")}</h1>
          <button data-testid="m-logout-btn" onClick={handleLogout} className="text-sm text-emerald-900">{t("logout")}</button>
        </div>
        <div className="md:hidden mb-6 grid grid-cols-3 gap-2">
          {TABS.map(({ id, label }) => (
            <button key={id} data-testid={`mtab-${id}`} onClick={() => setTab(id)} className={`text-xs px-2 py-2 rounded-md border ${tab === id ? "bg-emerald-900 text-white border-emerald-900" : "bg-white border-stone-200"}`}>{label}</button>
          ))}
        </div>

        {tab === "prayer" && <PrayerManager />}
        {tab === "announcements" && <AnnouncementsManager />}
        {tab === "photos" && <PhotosManager />}
        {tab === "events" && <EventsManager />}
        {tab === "committee" && <CommitteeManager />}
        {tab === "causes" && <CausesManager />}
        {tab === "payments" && <PaymentSettingsManager />}
        {tab === "donations" && <DonationsManager />}
        {tab === "content" && <ContentManager />}
        {tab === "security" && <SecuritySection />}
      </main>
    </div>
  );
}

// =============== Prayer Times ===============
function PrayerManager() {
  const [times, setTimes] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/prayer-times").then(r => setTimes(r.data)); }, []);
  const update = (k, v) => setTimes((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/prayer-times", times); toast.success("Prayer times saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!times) return <Loading />;
  const fields = ["fajr", "sunrise", "dhuhr", "asr", "maghrib", "isha", "jumuah"];
  return (
    <Section title="Prayer Times" subtitle="Update daily salah and Jumu'ah timings">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields.map((f) => (
          <div key={f}>
            <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block capitalize">{f}</label>
            <input
              data-testid={`prayer-input-${f}`}
              type="time" value={times[f] || ""} onChange={(e) => update(f, e.target.value)}
              className="w-full px-3 py-2.5 rounded-md border border-stone-300 focus:ring-2 focus:ring-emerald-700"
            />
          </div>
        ))}
      </div>
      <div className="mt-6">
        <button data-testid="save-prayer-times" onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Changes</button>
      </div>
    </Section>
  );
}

// =============== Announcements ===============
function AnnouncementsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title_en: "", title_ta: "", body_en: "", body_ta: "", active: true, pinned: false });
  const load = () => api.get("/announcements?only_active=false").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title_en) return toast.error("Title (English) required");
    try { await api.post("/announcements", form); setForm({ title_en: "", title_ta: "", body_en: "", body_ta: "", active: true, pinned: false }); toast.success("Announcement added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/announcements/${id}`); toast.success("Deleted"); load(); };
  const togglePin = async (it) => { await api.put(`/announcements/${it.id}`, { ...it, pinned: !it.pinned }); load(); };
  return (
    <Section title="Announcements" subtitle="Time-to-time messages shown on homepage ticker">
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Field label="Title (English)"><input data-testid="ann-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="inp" /></Field>
        <Field label="தலைப்பு (Tamil)"><input data-testid="ann-title-ta" value={form.title_ta} onChange={(e) => setForm({ ...form, title_ta: e.target.value })} className="inp" /></Field>
        <Field label="Body (English)"><input data-testid="ann-body-en" value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} className="inp" /></Field>
        <Field label="உள்ளடக்கம் (Tamil)"><input data-testid="ann-body-ta" value={form.body_ta} onChange={(e) => setForm({ ...form, body_ta: e.target.value })} className="inp" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Pin to top</label>
        <button data-testid="add-announcement-btn" onClick={create} className="btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Announcement</button>
      </div>
      <div className="space-y-3">
        {items.length === 0 ? <Empty msg="No announcements yet" /> : items.map((it, i) => (
          <div key={it.id} data-testid={`admin-ann-${i}`} className="bg-white border border-stone-200 rounded-lg p-4 flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {it.pinned && <Pin className="w-3.5 h-3.5 text-amber-700" />}
                <div className="font-medium text-emerald-950">{it.title_en}</div>
                {it.title_ta && <div className="text-sm text-stone-500 font-sans-ta">{it.title_ta}</div>}
              </div>
              {it.body_en && <p className="text-sm text-stone-600 mt-1">{it.body_en}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => togglePin(it)} className="text-xs px-2 py-1 border border-stone-300 rounded-md hover:bg-stone-50">{it.pinned ? "Unpin" : "Pin"}</button>
              <button data-testid={`del-ann-${i}`} onClick={() => del(it.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-md"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// =============== Photos ===============
function PhotosManager() {
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [captionEn, setCaptionEn] = useState("");
  const [captionTa, setCaptionTa] = useState("");
  const [order, setOrder] = useState(0);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/photos").then(r => setPhotos(r.data));
  useEffect(() => { load(); }, []);
  const upload = async () => {
    if (!file) return toast.error("Select a file");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(
        `/photos?caption_en=${encodeURIComponent(captionEn)}&caption_ta=${encodeURIComponent(captionTa)}&order=${order}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setFile(null); setCaptionEn(""); setCaptionTa(""); setOrder(0);
      toast.success("Photo uploaded");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/photos/${id}`); toast.success("Deleted"); load(); };
  return (
    <Section title="Photo Gallery" subtitle="Upload photos that will auto-scroll on the public site">
      <div className="bg-white border border-stone-200 rounded-lg p-5 mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Image File</label>
            <input data-testid="photo-file-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          </div>
          <Field label="Display Order"><input type="number" value={order} onChange={(e) => setOrder(parseInt(e.target.value || "0"))} className="inp" /></Field>
          <Field label="Caption (English)"><input data-testid="photo-cap-en" value={captionEn} onChange={(e) => setCaptionEn(e.target.value)} className="inp" /></Field>
          <Field label="தலைப்பு (Tamil)"><input data-testid="photo-cap-ta" value={captionTa} onChange={(e) => setCaptionTa(e.target.value)} className="inp" /></Field>
        </div>
        <div className="mt-5">
          <button data-testid="upload-photo-btn" onClick={upload} disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload Photo"}
          </button>
        </div>
      </div>
      {photos.length === 0 ? <Empty msg="No photos yet" /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p, i) => (
            <div key={p.id} data-testid={`admin-photo-${i}`} className="relative group bg-white border border-stone-200 rounded-lg overflow-hidden">
              <img src={photoFileUrl(p.id)} alt={p.caption_en || "photo"} className="w-full h-40 object-cover" />
              <div className="p-3">
                <div className="text-xs text-stone-700 truncate">{p.caption_en || "—"}</div>
                <div className="text-[10px] text-stone-400 mt-1">Order: {p.order}</div>
              </div>
              <button data-testid={`del-photo-${i}`} onClick={() => del(p.id)} className="absolute top-2 right-2 bg-white/95 text-red-600 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// =============== Events ===============
function EventsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title_en: "", title_ta: "", description_en: "", description_ta: "", event_date: "", location: "" });
  const load = () => api.get("/events").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title_en || !form.event_date) return toast.error("Title and date required");
    try { await api.post("/events", form); setForm({ title_en: "", title_ta: "", description_en: "", description_ta: "", event_date: "", location: "" }); toast.success("Event added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); };
  return (
    <Section title="Events & Programs" subtitle="Upcoming community programs and events">
      <div className="bg-white border border-stone-200 rounded-lg p-5 mb-6 grid md:grid-cols-2 gap-4">
        <Field label="Title (English)"><input data-testid="event-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="inp" /></Field>
        <Field label="தலைப்பு (Tamil)"><input data-testid="event-title-ta" value={form.title_ta} onChange={(e) => setForm({ ...form, title_ta: e.target.value })} className="inp" /></Field>
        <Field label="Date"><input data-testid="event-date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="inp" /></Field>
        <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="inp" /></Field>
        <Field label="Description (English)"><textarea value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="inp h-20" /></Field>
        <Field label="விளக்கம் (Tamil)"><textarea value={form.description_ta} onChange={(e) => setForm({ ...form, description_ta: e.target.value })} className="inp h-20" /></Field>
        <div className="md:col-span-2"><button data-testid="add-event-btn" onClick={create} className="btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Event</button></div>
      </div>
      {items.length === 0 ? <Empty msg="No events" /> : (
        <div className="space-y-3">
          {items.map((e, i) => (
            <div key={e.id} data-testid={`admin-event-${i}`} className="bg-white border border-stone-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-700">{e.event_date}</div>
                <div className="font-medium text-emerald-950">{e.title_en}</div>
                {e.location && <div className="text-sm text-stone-500">{e.location}</div>}
              </div>
              <button data-testid={`del-event-${i}`} onClick={() => del(e.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-md"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// =============== Committee ===============
function CommitteeManager() {
  const [items, setItems] = useState([]);
  const empty = { name_en: "", name_ta: "", designation_en: "", designation_ta: "", phone: "", email: "", order: 0 };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/committee").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.name_en || !form.designation_en) return toast.error("Name and designation (English) are required");
    try {
      if (editingId) {
        await api.put(`/committee/${editingId}`, form);
        toast.success("Member updated");
      } else {
        await api.post("/committee", form);
        toast.success("Member added");
      }
      setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({ name_en: m.name_en, name_ta: m.name_ta || "", designation_en: m.designation_en, designation_ta: m.designation_ta || "", phone: m.phone || "", email: m.email || "", order: m.order || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditingId(null); setForm(empty); };
  const del = async (id) => { await api.delete(`/committee/${id}`); toast.success("Deleted"); load(); };
  return (
    <Section title="Management Committee" subtitle="Add committee members with their designation and contact details">
      <div className="bg-white border border-stone-200 rounded-lg p-5 mb-6 grid md:grid-cols-2 gap-4">
        <Field label="Name (English)"><input data-testid="cm-name-en" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="inp" placeholder="e.g. Mohammed Yusuf" /></Field>
        <Field label="பெயர் (Tamil)"><input data-testid="cm-name-ta" value={form.name_ta} onChange={(e) => setForm({ ...form, name_ta: e.target.value })} className="inp" /></Field>
        <Field label="Designation (English)"><input data-testid="cm-role-en" value={form.designation_en} onChange={(e) => setForm({ ...form, designation_en: e.target.value })} className="inp" placeholder="e.g. President, Secretary, Treasurer" /></Field>
        <Field label="பதவி (Tamil)"><input data-testid="cm-role-ta" value={form.designation_ta} onChange={(e) => setForm({ ...form, designation_ta: e.target.value })} className="inp" /></Field>
        <Field label="Phone Number"><input data-testid="cm-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" placeholder="+91 98765 43210" /></Field>
        <Field label="Email (optional)"><input data-testid="cm-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" /></Field>
        <Field label="Display Order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0") })} className="inp" /></Field>
        <div className="md:col-span-2 flex items-center gap-3">
          <button data-testid="save-committee-btn" onClick={submit} className="btn-primary inline-flex items-center gap-2">
            {editingId ? <><Save className="w-4 h-4" /> Update Member</> : <><Plus className="w-4 h-4" /> Add Member</>}
          </button>
          {editingId && <button onClick={cancelEdit} className="btn-ghost-emerald">Cancel</button>}
        </div>
      </div>
      {items.length === 0 ? <Empty msg="No committee members yet" /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((m, i) => (
            <CommitteeMemberRow key={m.id} m={m} i={i} onChanged={load} onEdit={() => startEdit(m)} onDelete={() => del(m.id)} />
          ))}
        </div>
      )}
    </Section>
  );
}

function CommitteeMemberRow({ m, i, onChanged, onEdit, onDelete }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/committee/${m.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      onChanged();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) doUpload(file);
    e.target.value = "";
  };

  const removePhoto = async () => {
    setBusy(true);
    try { await api.delete(`/committee/${m.id}/photo`); toast.success("Photo removed"); onChanged(); }
    catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`admin-committee-${i}`} className="bg-white border border-stone-200 rounded-xl p-4 flex items-start gap-4">
      {/* Avatar */}
      <div className="shrink-0">
        {m.photo_path ? (
          <img
            src={`${committeePhotoUrl(m.id)}?v=${encodeURIComponent(m.photo_path)}`}
            alt=""
            className="w-16 h-16 rounded-full object-cover ring-2 ring-emerald-900/20"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-stone-100 border-2 border-dashed border-stone-300 grid place-items-center text-stone-400 text-[10px] text-center leading-tight px-1">
            No<br/>photo
          </div>
        )}
      </div>

      {/* Details + drop zone */}
      <div className="min-w-0 flex-1">
        <div className="font-medium text-emerald-950 truncate">{m.name_en}</div>
        {m.name_ta && <div className="text-xs text-stone-500 font-sans-ta truncate">{m.name_ta}</div>}
        <div className="text-xs uppercase tracking-widest text-amber-700 mt-0.5">{m.designation_en}</div>
        {m.phone && <div className="text-sm text-stone-600 mt-1">{m.phone}</div>}
        {m.email && <div className="text-xs text-stone-500 truncate">{m.email}</div>}
        <div className="text-[10px] text-stone-400 mt-0.5">Order: {m.order}</div>

        {/* ── Drag-and-drop zone ── */}
        <input
          ref={inputRef}
          data-testid={`cm-photo-input-${i}`}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          onChange={handleChange}
          className="hidden"
        />
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => !busy && inputRef.current?.click()}
          data-testid={`cm-photo-btn-${i}`}
          className={[
            "mt-3 rounded-lg border-2 border-dashed px-3 py-2.5 text-center transition-all select-none",
            busy
              ? "opacity-50 cursor-not-allowed border-stone-200 bg-stone-50"
              : dragging
              ? "border-emerald-500 bg-emerald-50 scale-[1.01] cursor-copy"
              : "border-stone-300 hover:border-emerald-600 hover:bg-emerald-50/50 cursor-pointer",
          ].join(" ")}
        >
          {busy ? (
            <div className="flex items-center justify-center gap-1.5 text-xs text-stone-400">
              <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" opacity="0.75"/>
              </svg>
              Uploading…
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1.5 text-xs text-stone-500">
              <Upload className="w-3.5 h-3.5 shrink-0 text-emerald-700" />
              <span className={dragging ? "text-emerald-700 font-medium" : ""}>
                {m.photo_path ? "Drop to replace · or click" : "Drop photo here · or click to upload"}
              </span>
            </div>
          )}
        </div>

        {m.photo_path && !busy && (
          <button
            data-testid={`cm-photo-remove-${i}`}
            onClick={removePhoto}
            className="mt-1 text-[10px] text-stone-400 hover:text-red-500 transition-colors"
          >
            Remove photo
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 shrink-0">
        <button data-testid={`edit-cm-${i}`} onClick={onEdit} className="text-xs px-2 py-1 border border-stone-300 rounded-md hover:bg-stone-50">Edit</button>
        <button data-testid={`del-cm-${i}`} onClick={onDelete} className="text-red-600 hover:bg-red-50 p-1.5 rounded-md"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// =============== Site Content ===============
function ContentManager() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/site-content").then(r => setContent(r.data)); }, []);
  const update = (k, v) => setContent((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/site-content", content); toast.success("Saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!content) return <Loading />;
  const FIELDS = [
    ["hero_title_en", "Hero Title (EN)"], ["hero_title_ta", "Hero Title (TA)"],
    ["hero_subtitle_en", "Hero Subtitle (EN)"], ["hero_subtitle_ta", "Hero Subtitle (TA)"],
    ["about_en", "About (EN)", true], ["about_ta", "About (TA)", true],
    ["madrasa_info_en", "Madrasa Info (EN)", true], ["madrasa_info_ta", "Madrasa Info (TA)", true],
    ["madrasa_timings", "Madrasa Timings"],
    ["donations_info_en", "Donations Info (EN)", true], ["donations_info_ta", "Donations Info (TA)", true],
    ["bank_name", "Bank Name"], ["account_name", "Account Name"], ["account_number", "Account Number"],
    ["ifsc", "IFSC"], ["upi_id", "UPI ID"],
    ["address", "Address"], ["phone", "Phone"], ["email", "Email"],
    ["latitude", "Map Latitude (optional)"], ["longitude", "Map Longitude (optional)"],
  ];
  return (
    <Section title="Site Content" subtitle="Edit all general site content (hero, about, madrasa, contact, donations)">
      <div className="grid md:grid-cols-2 gap-4">
        {FIELDS.map(([key, label, isLong]) => (
          <Field key={key} label={label} full={!!isLong}>
            {isLong ? (
              <textarea data-testid={`ct-${key}`} value={content[key] || ""} onChange={(e) => update(key, e.target.value)} className="inp h-24" />
            ) : (
              <input data-testid={`ct-${key}`} value={content[key] || ""} onChange={(e) => update(key, e.target.value)} className="inp" />
            )}
          </Field>
        ))}
      </div>
      <div className="mt-6"><button data-testid="save-content-btn" onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Changes</button></div>
    </Section>
  );
}

// =============== Helpers ===============
function Section({ title, subtitle, children }) {
  return (
    <div data-testid={`section-${title}`} className="fade-up">
      <div className="mb-6">
        <h2 className="font-serif-en text-3xl text-emerald-950">{title}</h2>
        {subtitle && <p className="text-stone-500 text-sm mt-1">{subtitle}</p>}
      </div>
      <style>{`.inp{width:100%;padding:0.6rem 0.8rem;border-radius:0.375rem;border:1px solid #d6d3d1;background:#fff}.inp:focus{outline:none;box-shadow:0 0 0 2px #064E3B33;border-color:#064E3B}`}</style>
      {children}
    </div>
  );
}
function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
function Empty({ msg }) { return <div className="text-center text-stone-500 py-10 bg-white border border-dashed border-stone-300 rounded-lg">{msg}</div>; }
function Loading() { return <div className="text-stone-500 text-sm">Loading…</div>; }
